import { useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { IngredientSummary } from '@fewerbags/schema';
import { api, queryKeys } from '../../lib/api';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

/**
 * Ingredient search-as-you-type. The text field is free text (callers may keep
 * whatever the user typed as a fallback name); picking a suggestion reports the
 * full `IngredientSummary` via `onSelect`.
 */
export function IngredientAutocomplete({
  id,
  label,
  value,
  onChange,
  onSelect,
  excludeId,
  placeholder,
  error,
  helpText,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  onSelect: (ingredient: IngredientSummary) => void;
  /** Hide this ingredient id from results (e.g. merging: don't suggest itself). */
  excludeId?: string;
  placeholder?: string;
  error?: string | undefined;
  helpText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listId = useId();
  const debounced = useDebouncedValue(value.trim(), 250);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useQuery({
    queryKey: queryKeys.ingredientSearch(debounced),
    queryFn: () => api.ingredients.search(debounced, 10),
    enabled: debounced.length > 0,
    staleTime: 30_000,
  });

  const results = (search.data?.items ?? []).filter((i) => i.id !== excludeId);

  function pick(ing: IngredientSummary) {
    onSelect(ing);
    setOpen(false);
  }

  return (
    <div className="relative flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : helpText ? `${id}-help` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        className={`min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted ${error ? 'border-fail' : ''}`}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Let a click on an option register before the list disappears.
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={(e) => {
          if (!open || results.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            const hit = results[highlight];
            if (hit) {
              e.preventDefault();
              pick(hit);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {helpText && !error ? (
        <p id={`${id}-help`} className="text-xs text-muted">
          {helpText}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-fail">
          {error}
        </p>
      ) : null}
      {open && results.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg"
        >
          {results.map((ing, i) => (
            <li key={ing.id} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(ing)}
                className={`flex min-h-11 w-full flex-col items-start px-3 py-1.5 text-left text-sm ${
                  i === highlight ? 'bg-accent-soft' : ''
                }`}
              >
                <span className="font-medium text-ink">{ing.canonicalName}</span>
                {ing.matchedAlias ? (
                  <span className="text-xs text-muted">matches “{ing.matchedAlias}”</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
